import React, { useEffect, useRef, useState } from 'react';
import OrderOptionCard from 'components/order-option-card';
import GroupOrderIcon from 'assets/images/group-order.png';
import {
  DateRangeSelector,
  Dropdown,
  DropdownItem,
  DropdownList,
  ENUM_DATE_RANGE,
  ToastMessage,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { FilterAltIcon } from 'cokitchen-icons';
import { ENUM_ORDER_STATUS } from 'utils/enum';
import SearchInput from 'components/base/search-input';
import ConfirmLocationModal from 'components/modals/confirm-location-modal';
import useOrdersContext from 'contexts/orders.context';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import OrderCard from 'components/order-card';
import InfiniteScroll from 'react-infinite-scroll-component';
import PeopleIcon from 'assets/images/happy-people.png';
import ScheduledDeliveryModal from 'components/modals/scheduled-delivery-modal';
import { Bars } from 'react-loader-spinner';
import NewGroupOrderModal from 'components/modals/group-order/new-group-order-modal';
import { toast } from 'react-toastify';
import { handleApiError } from 'utils/functions';
import { OrderType } from 'models/order.model';

const filterList = [
  '',
  ENUM_ORDER_STATUS.ONGOING,
  ENUM_ORDER_STATUS.COMPLETED,
  ENUM_ORDER_STATUS.CANCELLED,
  ENUM_ORDER_STATUS.REJECTED
];

const Orders = () => {
  const mounting = useRef(true);
  const {
    activeCompany,
    setIsBasketDisabled,
    setCalculatedOrder: setDashboardCalculatedOrder,
    setIsBackLink
  } = useDashboardContext();
  const [showFilterList, setShowFilterList] = useState<boolean>(false);
  const [showConfirmLocationModal, setShowConfirmLocationModal] = useState<boolean>(false);
  const [showScheduledDeliveryModal, setShowScheduledDeliveryModal] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const {
    calculatedOrder,
    setCalculatedOrder,
    setcalculatedOrderForm,
    createGroupOrderObject,
    calculatedOrderForm,
    handleReset
  } = useOrdersContext();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [filter, setFilter] = useState({
    start_date: getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK).startDate.toISOString(),
    end_date: getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK).endDate.toISOString(),
    status: '',
    search: '',
    page: 1,
    page_size: 5
  });
  const [showGroupOrderModal, setShowGroupOrderModal] = useState(false);
  const [isGroupOrder, setIsGroupOrder] = useState(false);
  const [isBatchOrder, setIsBatchOrder] = useState(false);

  const getOrderHistory = async (reload?: boolean) => {
    if (reload) setLoading(true);
    try {
      const params: Record<string, unknown> = {
        company_id: activeCompany.id,
        start_date: filter.start_date,
        end_date: filter.end_date,
        page: filter.page,
        page_size: filter.page_size
      };
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const res = await apiService.get('business/order-history', {
        params,
        baseURL: process.env.REACT_APP_ORDER_API_URL
      });
      const results = res.data.data.orders.results.concat(
        res.data.data.scheduled_orders.results,
        res.data.data.preorder.results
      );
      if (filter.page > 1) setOrders([...orders, ...results]);
      else setOrders(results);
      setTotalOrders(+res.data.data.scheduled_orders.total + +res.data.data.orders.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilter({ ...filter, search, page: 1 });
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (mounting.current || filter.page === 1) getOrderHistory(true);
    return () => {
      mounting.current = false;
    };
  }, [filter.search, filter.start_date, filter.end_date, filter.status]);

  useEffect(() => {
    if (filter.page > 1) getOrderHistory();
  }, [filter.page]);

  useEffect(() => {
    const hasScroll = document.body.scrollHeight - window.innerHeight >= 80;
    if (!loading && orders.length < totalOrders && !hasScroll)
      setFilter((f) => ({ ...f, page: ++f.page }));
  }, [orders, totalOrders]);

  useEffect(() => {
    setDashboardCalculatedOrder(calculatedOrder);
  }, [calculatedOrder]);

  useEffect(() => {
    setIsBackLink(false);
    setIsGroupOrder(false);
    setIsBatchOrder(false);
    setIsBasketDisabled(true);
    return () => setIsBasketDisabled(false);
  }, []);

  const createGroupOrder = async (scheduledDetails: {
    recurring: boolean;
    scheduled_delivery_time: string;
    scheduled_delivery_date: string;
    selected_days: string[];
    recurrence: string;
    scheduled: boolean;
    recurring_order_end_date: string;
  }) => {
    try {
      const { scheduled, scheduled_delivery_date, scheduled_delivery_time } = scheduledDetails;
      setLoading(true);
      const res = await apiService.post('company/order/group', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: {
          ...createGroupOrderObject,
          scheduled,
          scheduled_delivery_date,
          scheduled_delivery_time
        }
      });
      if (res.data.calculated_order) {
        setCalculatedOrder(res.data.calculated_order);
        setShowScheduledDeliveryModal(false);
      }

      toast(<ToastMessage type='success' message='Group order created successfully' />);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-8'>
      <div className='grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3'>
        <OrderOptionCard
          image={<img className='max-w-[100px]' src={PeopleIcon} />}
          title='Batch order'
          description='Foster team efficiency by placing bulk orders from FCB Brands. Orders are confirmed and delivered if they meet the minimum requirement before the cutoff time.'
          link={'#'}
          onClick={() => {
            setShowConfirmLocationModal(true);
            setIsBasketDisabled(false);
            setIsBatchOrder(true);
            setIsGroupOrder(false);
            handleReset();
          }}
        />
        <OrderOptionCard
          image={<img className='max-w-[100px]' src={PeopleIcon} />}
          title='Group order'
          description='Foster team unity with FoodCourt Business Group Ordering! Each order is scheduled at least 4 hours in advance for an effective culinary experience.'
          link={`#`}
          onClick={() => {
            setShowGroupOrderModal(true);
            setIsBasketDisabled(false);
            setIsGroupOrder(true);
            setIsBatchOrder(false);
            handleReset();
          }}
        />
        <OrderOptionCard
          title='Bulk Order'
          description='Schedule bulk orders with FoodCourt Business Bulk Ordering! Each order is scheduled at least 4 hours in advance for an effective culinary experience.'
          image={<img className='max-w-[100px]' src={GroupOrderIcon} />}
          link={`#`}
          onClick={() => {
            setShowConfirmLocationModal(true);
            setIsBasketDisabled(false);
            setIsGroupOrder(false);
            setIsBatchOrder(false);
            handleReset();
          }}
        />
      </div>

      <div className='flex flex-col gap-6'>
        <p className='text-lg font-medium'>Order history</p>
        <div className='flex items-center gap-3'>
          <DateRangeSelector
            defaultRange={ENUM_DATE_RANGE.THIS_WEEK}
            onChange={(v) =>
              setFilter({
                ...filter,
                start_date: v.date.startDate.toISOString(),
                end_date: v.date.endDate.toISOString(),
                page: 1
              })
            }
            className='h-[36px] text-[#5E6164] border-[#E5E6E6]'
          />
          <Dropdown
            visible={showFilterList}
            onClickOutside={() => setShowFilterList(false)}
            content={
              <div
                className='flex justify-between items-center min-w-[120px] border border-[#E5E6E6] rounded-md p-2 text-[#5E6164] text-sm gap-2 cursor-pointer h-[36px] truncate capitalize'
                onClick={() => setShowFilterList(true)}>
                {filter.status ? `${filter.status}` : 'All'}
                <FilterAltIcon />
              </div>
            }>
            <DropdownList>
              {filterList.map((item, index) => (
                <DropdownItem
                  key={index}
                  onClick={() => {
                    setFilter({ ...filter, status: item, page: 1 });
                    setShowFilterList(false);
                  }}>
                  <p className='capitalize'>{item || 'All'}</p>
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
          <div className='ml-auto'>
            <SearchInput value={search} handleChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        {loading ? (
          <div className='grow w-full h-screen flex justify-center pt-[20vh]'>
            <Bars color='#E60A2B' height={50} width={50} />
          </div>
        ) : orders?.length ? (
          <InfiniteScroll
            hasMore={totalOrders > orders?.length}
            dataLength={orders?.length}
            loader={<p className='text-center mt-8'>Loading...</p>}
            next={() => setFilter((f) => ({ ...f, page: ++f.page }))}>
            <div className='grid gap-6 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]'>
              {orders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          <div className='h-40 flex items-center justify-center'>
            <p className='flex justify-center'>No orders found</p>
          </div>
        )}
      </div>

      <ConfirmLocationModal
        isOpen={showConfirmLocationModal}
        onClose={() => {
          setShowConfirmLocationModal(false);
        }}
        setShowDeliveryModal={setShowScheduledDeliveryModal}
        isBatchOrder={isBatchOrder}
      />

      <ScheduledDeliveryModal
        handleClick={(k) => {
          setcalculatedOrderForm({ ...calculatedOrderForm, ...k });
          if (isGroupOrder) {
            createGroupOrder(k);
            setIsBasketDisabled(false);
          }
        }}
        loading={loading}
        isOpen={showScheduledDeliveryModal}
        onClose={() => setShowScheduledDeliveryModal(false)}
        type={isGroupOrder ? 'group' : 'bulk'}
      />

      <NewGroupOrderModal
        open={showGroupOrderModal}
        onClose={() => {
          setShowGroupOrderModal(false);
        }}
        setShowDeliveryModal={setShowScheduledDeliveryModal}
      />
    </div>
  );
};

export default Orders;
